import { supabaseGestionPension as supabase } from '../config/settings/supabase-client'
import {
  DB_METHOD,
  LEGACY_METHOD,
  METHOD,
  normalizePensionMethod,
  normalizeDecimalInput,
  validateUtmQuantity,
} from '../config/settings/constants'
import { getLogger } from '../core/debuggers'

const logger = getLogger('pensionConfig.slice')

const mapConfigFromDb = (data) => {
  if (!data) return null

  const method = normalizePensionMethod(data.method)

  // UTM quantity lives in `percentage`; expose it as `amount` for the form.
  if (method === METHOD.UTM || data.method === LEGACY_METHOD.PERCENTAGE) {
    return {
      ...data,
      method: METHOD.UTM,
      amount: data.percentage ?? data.amount,
      percentage: null,
      payment_day: data.payment_day ?? null,
    }
  }

  return {
    ...data,
    method,
    payment_day: data.payment_day ?? null,
  }
}

/**
 * Fetch the active pension config for the logged-in user
 */
export const fetchPensionConfig = async (userId) => {
  const { data, error } = await supabase
    .from('pension_configs')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .maybeSingle()

  if (error) {
    logger.error('error:', error)
    throw error
  }

  return mapConfigFromDb(data)
}

/**
 * Update or insert the pension configuration for the user
 * and mark onboarding as completed.
 *
 * `allowMethodChange` is for the admin ops ficha only: it skips the
 * payer rule that forbids leaving UTM for IMM or fixed amount.
 */
export const savePensionConfig = async ({
  userId,
  method,
  amount,
  percentage,
  paymentDay,
  allowMethodChange = false,
}) => {
  const uiMethod = normalizePensionMethod(method)

  // Once a config is on UTM, do not allow downgrade to IMM or fixed amount.
  const existing = await fetchPensionConfig(userId)
  if (
    !allowMethodChange &&
    existing &&
    normalizePensionMethod(existing.method) === METHOD.UTM &&
    uiMethod !== METHOD.UTM
  ) {
    throw new Error(
      'Tu pensión ya está en UTM. No puedes volver a un método de cálculo antiguo.'
    )
  }

  const resolvedValue =
    amount !== '' && amount != null
      ? amount
      : percentage !== '' && percentage != null
        ? percentage
        : null

  const resolvedPaymentDay =
    paymentDay === '' || paymentDay == null ? null : Number(paymentDay)

  const payload = {
    user_id: userId,
    method: null,
    amount: null,
    percentage: null,
    payment_day: Number.isFinite(resolvedPaymentDay) ? resolvedPaymentDay : null,
    setup_completed: true,
    is_active: true,
  }

  if (uiMethod === METHOD.UTM) {
    const utmValidation = validateUtmQuantity(resolvedValue)
    if (!utmValidation.valid) {
      throw new Error(utmValidation.error)
    }
    payload.method = DB_METHOD.UTM
    payload.percentage = normalizeDecimalInput(resolvedValue)
    payload.amount = null
  } else if (uiMethod === METHOD.IMM) {
    payload.method = DB_METHOD.IMM
    payload.percentage = normalizeDecimalInput(resolvedValue)
    payload.amount = null
  } else if (uiMethod === METHOD.FIXED_AMOUNT) {
    const parsedAmount = Number(normalizeDecimalInput(resolvedValue))
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      throw new Error('Ingresa un monto en pesos mayor a 0.')
    }
    payload.method = DB_METHOD.FIXED_AMOUNT
    payload.amount = parsedAmount
    payload.percentage = null
  }

  const { data: pensionConfig, error: pensionError } = await supabase
    .from('pension_configs')
    .upsert(payload, { onConflict: 'user_id' })
    .select()
    .single()

  logger.debug('pensionError', pensionError)

  if (pensionError) throw pensionError

  const { error: profileError } = await supabase
    .from('profiles')
    .update({
      onboarding_status: 'completed',
    })
    .eq('id', userId)

  if (profileError) throw profileError

  return mapConfigFromDb(pensionConfig)
}

/**
 * Latest IMM (Ingreso Mínimo Mensual) row.
 * @returns {Promise<{ id: string, value: number, effectiveFrom: string, source: string|null }|null>}
 */
export const fetchLatestImm = async () => {
  const { data, error } = await supabase
    .from('imm_history')
    .select('id, value, effective_from, source')
    .order('effective_from', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    logger.error('fetchLatestImm error:', error)
    throw error
  }

  if (!data) return null

  return {
    id: data.id,
    value: Number(data.value),
    effectiveFrom: data.effective_from,
    source: data.source || null,
  }
}

/**
 * Full IMM history, newest first.
 * @returns {Promise<Array<{ id: string, value: number, effectiveFrom: string, source: string|null }>>}
 */
export const fetchImmHistory = async () => {
  const { data, error } = await supabase
    .from('imm_history')
    .select('id, value, effective_from, source')
    .order('effective_from', { ascending: false })

  if (error) {
    logger.error('fetchImmHistory error:', error)
    throw error
  }

  return (data || []).map((row) => ({
    id: row.id,
    value: Number(row.value),
    effectiveFrom: row.effective_from,
    source: row.source || null,
  }))
}
